import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
    LocalNotifications,
    LocalNotificationSchema,
} from '@capacitor/local-notifications';
import { IncomingCall } from '../plugins/incoming-call.plugin';
import { TwilioVoice } from '../plugins/twilio-voice.plugin';

type NotificationScope = 'alerts' | 'calls';
const CALLS_CHANNEL_ID = 'connect-calls-v2';
const DUPLICATE_NOTIFICATION_WINDOW_MS = 2500;
const BROWSER_NOTIFICATION_AUTO_CLOSE_MS = 7000;

@Injectable({
    providedIn: 'root',
})
export class NativeNotificationService {
    private listenersRegistered = false;
    private permissionRequest?: Promise<void>;
    private browserPermissionRequest?: Promise<boolean>;
    private browserPermissionListenersRegistered = false;
    private browserAudioUnlockListenersRegistered = false;
    private browserAudioElement?: HTMLAudioElement;
    private browserAudioUnlocked = false;
    private appStateListenersRegistered = false;
    private recentNotificationTimes = new Map<string, number>();

    constructor(
        private router: Router,
        private ngZone: NgZone
    ) {}

    isNativePlatform(): boolean {
        return Capacitor.isNativePlatform();
    }

    isNativeAndroid(): boolean {
        return this.isNativePlatform() && Capacitor.getPlatform() === 'android';
    }

    async init(): Promise<void> {
        if (!this.isNativePlatform()) {
            this.registerBrowserPermissionPrompt();
            this.registerBrowserAudioUnlock();
            return;
        }

        if (this.isNativeAndroid()) {
            await this.createChannels();
        }

        if (this.listenersRegistered) {
            return;
        }

        this.listenersRegistered = true;

        await LocalNotifications.addListener(
            'localNotificationActionPerformed',
            (event: any) => {
                const conversationId = this.getConversationId(event?.notification);
                const route = event?.notification?.extra?.route;
                console.log(
                    `[LocalNotificationRoute] Tap received. conversationId=${conversationId || '<none>'}, route=${route || '<none>'}`
                );

                if (conversationId) {
                    this.navigateToConversation(conversationId);
                    return;
                }

                if (route) {
                    this.router.navigate([route]).catch((error) => {
                        console.error('Failed to route notification action:', error);
                    });
                }
            }
        );
        console.log('[LocalNotificationRoute] Local notification action listener registered.');

        if (!this.appStateListenersRegistered) {
            this.appStateListenersRegistered = true;

            await App.addListener('appStateChange', ({ isActive }) => {
                if (isActive) {
                    void this.requestStartupPermissions(true);
                }
            });

            await App.addListener('resume', () => {
                void this.requestStartupPermissions(true);
            });
        }
    }

    async requestStartupPermissions(force = false): Promise<void> {
        if (!this.isNativePlatform()) {
            this.registerBrowserPermissionPrompt();
            this.registerBrowserAudioUnlock();
            return;
        }

        if (!force && this.permissionRequest) {
            await this.permissionRequest;
            return;
        }

        this.permissionRequest = this.performStartupPermissionRequest();

        try {
            await this.permissionRequest;
        } finally {
            this.permissionRequest = undefined;
        }
    }

    async notifyAlert(
        message: string,
        kind: 'sms' | 'email' | 'leads' | 'alert' = 'alert',
        conversationId?: string | number | null
    ): Promise<void> {
        if (!this.shouldShowSystemNotification()) {
            return;
        }

        if (this.shouldSuppressDuplicateNotification(kind, conversationId)) {
            return;
        }

        const titleMap = {
            sms: 'New SMS',
            email: 'New Email',
            leads: 'New Leads',
            alert: 'New Alert',
        };

        if (!this.isNativePlatform()) {
            void this.playBrowserNotificationSound();
            await this.showBrowserNotification({
                title: titleMap[kind],
                body: message,
                kind,
                conversationId,
                route: this.getNotificationRoute(kind, conversationId),
            });
            return;
        }

        await this.scheduleNotification({
            id: this.generateId(),
            title: titleMap[kind],
            body: message,
            channelId: 'connect-alerts',
            extra: {
                scope: 'alerts',
                route: this.getNotificationRoute(kind, conversationId),
                kind,
                conversationId: conversationId || null,
            },
        });
    }

    async notifyIncomingCall(callData: any): Promise<void> {
        if (!this.isNativeAndroid() || !this.shouldShowSystemNotification()) {
            return;
        }

        try {
            await IncomingCall.showIncomingCall({
                callerName:
                    callData?.caller_name ||
                    callData?.customer_name ||
                    callData?.from_number ||
                    'Incoming caller',
                callerNumber: callData?.from_number || callData?.caller_number || '',
                conversationId: callData?.conversation_id ? `${callData.conversation_id}` : null,
                callSid: callData?.call_sid || null,
                dealerId: callData?.dealer_id ? `${callData.dealer_id}` : null,
                toNumber: callData?.to_number || null,
            });
            return;
        } catch (error) {
            console.error('Failed to show native incoming call UI:', error);
        }

        const caller =
            callData?.caller_name ||
            callData?.customer_name ||
            callData?.from_number ||
            'Incoming caller';

        const body = callData?.message || `Incoming call from ${caller}`;

        await this.scheduleNotification({
            id: this.generateId(),
            title: 'Incoming Call',
            body,
            channelId: CALLS_CHANNEL_ID,
            extra: {
                scope: 'calls',
                route: '/conversations',
                conversationId: callData?.conversation_id || null,
                callSid: callData?.call_sid || null,
            },
        });
    }

    async clearAlertNotifications(): Promise<void> {
        await this.clearNotificationsByScope('alerts');
    }

    async clearCallNotifications(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        try {
            await IncomingCall.dismissIncomingCall();
        } catch (error) {
            console.error('Failed to dismiss native incoming call UI:', error);
        }
        await this.clearNotificationsByScope('calls');
    }

    shouldShowSystemNotification(): boolean {
        return this.isNativePlatform() || this.isBrowserNotificationSupported();
    }

    async ensureCallPermissions(): Promise<boolean> {
        if (!this.isNativeAndroid()) {
            return true;
        }

        await this.ensureNotificationPermission();
        return this.ensureMicrophonePermission();
    }

    private async createChannels(): Promise<void> {
        try {
            await LocalNotifications.createChannel({
                id: 'connect-alerts',
                name: 'Connect Alerts',
                description: 'SMS, email, and alert notifications',
                importance: 5,
                visibility: 1,
            });

            await LocalNotifications.createChannel({
                id: CALLS_CHANNEL_ID,
                name: 'Connect Calls',
                description: 'Incoming call notifications',
                importance: 5,
                visibility: 1,
                sound: 'default',
            });
        } catch (error) {
            console.error('Failed to create Android notification channels:', error);
        }
    }

    private async performStartupPermissionRequest(): Promise<void> {
        await this.ensureNotificationPermission();
        await this.ensureMicrophonePermission();
    }

    private async ensureNotificationPermission(): Promise<void> {
        try {
            const permissionStatus = await LocalNotifications.checkPermissions();
            if (permissionStatus.display !== 'granted') {
                await LocalNotifications.requestPermissions();
            }
        } catch (error) {
            console.error('Failed to request notification permission:', error);
        }
    }

    private async ensureBrowserNotificationPermission(force = false): Promise<boolean> {
        if (!this.isBrowserNotificationSupported()) {
            return false;
        }

        if (Notification.permission === 'granted') {
            return true;
        }

        if (Notification.permission === 'denied') {
            return false;
        }

        if (!force && this.browserPermissionRequest) {
            return this.browserPermissionRequest;
        }

        this.browserPermissionRequest = Notification.requestPermission()
            .then((permission) => permission === 'granted')
            .catch((error) => {
                console.error('Failed to request browser notification permission:', error);
                return false;
            });

        try {
            return await this.browserPermissionRequest;
        } finally {
            this.browserPermissionRequest = undefined;
        }
    }

    private isBrowserNotificationSupported(): boolean {
        return typeof window !== 'undefined' && 'Notification' in window;
    }

    private async showBrowserNotification(options: {
        title: string;
        body: string;
        kind: 'sms' | 'email' | 'leads' | 'alert';
        conversationId?: string | number | null;
        route: string;
    }): Promise<void> {
        const granted = await this.ensureBrowserNotificationPermission();
        if (!granted) {
            return;
        }

        try {
            const notification = new Notification(options.title, {
                body: options.body || options.title,
                icon: '/assets/icon/icon-192x192.png',
                badge: '/assets/icon/icon-192x192.png',
                renotify: true,
                silent: false,
                timestamp: Date.now(),
                tag: this.browserNotificationTag(options.kind, options.conversationId, options.body),
            });

            window.setTimeout(() => {
                notification.close();
            }, BROWSER_NOTIFICATION_AUTO_CLOSE_MS);

            notification.onclick = () => {
                window.focus();

                if (options.kind !== 'leads' && options.conversationId) {
                    this.router.navigate(['conversations'], {
                        queryParams: { id: options.conversationId },
                    }).catch((error) => {
                        console.error('Failed to route browser notification:', error);
                    });
                    notification.close();
                    return;
                }

                this.router.navigate([options.route]).catch((error) => {
                    console.error('Failed to route browser notification:', error);
                });
                notification.close();
            };
        } catch (error) {
            console.error('Failed to show browser notification:', error);
        }
    }

    private browserNotificationTag(
        kind: string,
        conversationId?: string | number | null,
        message?: string
    ): string {
        const uniqueSuffix = `${Date.now()}-${Math.floor(Math.random() * 100000)}`;

        if (conversationId) {
            return `connect-${kind}-${conversationId}-${uniqueSuffix}`;
        }

        return `connect-${kind}-${this.hashText(message || '')}-${uniqueSuffix}`;
    }

    private shouldSuppressDuplicateNotification(
        kind: 'sms' | 'email' | 'leads' | 'alert',
        conversationId?: string | number | null
    ): boolean {
        const now = Date.now();
        const keys = [
            `kind:${kind}`,
        ];

        if (conversationId) {
            keys.push(`conversation:${kind}:${conversationId}`);
        }

        this.recentNotificationTimes.forEach((timestamp, key) => {
            if (now - timestamp > DUPLICATE_NOTIFICATION_WINDOW_MS) {
                this.recentNotificationTimes.delete(key);
            }
        });

        const isDuplicate = keys.some((key) => {
            const lastShownAt = this.recentNotificationTimes.get(key);
            return !!lastShownAt && now - lastShownAt <= DUPLICATE_NOTIFICATION_WINDOW_MS;
        });

        keys.forEach((key) => {
            this.recentNotificationTimes.set(key, now);
        });

        return isDuplicate;
    }

    private getNotificationRoute(
        kind: 'sms' | 'email' | 'leads' | 'alert',
        conversationId?: string | number | null
    ): string {
        if (kind === 'leads') {
            return '/leads';
        }

        return conversationId ? '/conversations' : '/alerts';
    }

    private hashText(value: string): string {
        let hash = 0;

        for (let i = 0; i < value.length; i++) {
            hash = ((hash << 5) - hash) + value.charCodeAt(i);
            hash |= 0;
        }

        return `${Math.abs(hash) || Date.now()}`;
    }

    private registerBrowserPermissionPrompt(): void {
        if (
            this.browserPermissionListenersRegistered ||
            !this.isBrowserNotificationSupported() ||
            Notification.permission !== 'default'
        ) {
            return;
        }

        this.browserPermissionListenersRegistered = true;

        const requestOnUserGesture = () => {
            window.removeEventListener('pointerdown', requestOnUserGesture);
            window.removeEventListener('keydown', requestOnUserGesture);

            this.ensureBrowserNotificationPermission(true).then((granted) => {
                if (!granted && Notification.permission === 'default') {
                    this.browserPermissionListenersRegistered = false;
                    this.registerBrowserPermissionPrompt();
                }
            });
        };

        window.addEventListener('pointerdown', requestOnUserGesture, { once: true });
        window.addEventListener('keydown', requestOnUserGesture, { once: true });
    }

    private registerBrowserAudioUnlock(): void {
        if (
            this.browserAudioUnlocked ||
            this.browserAudioUnlockListenersRegistered ||
            typeof window === 'undefined'
        ) {
            return;
        }

        this.browserAudioUnlockListenersRegistered = true;

        const unlockOnUserGesture = () => {
            window.removeEventListener('pointerdown', unlockOnUserGesture);
            window.removeEventListener('keydown', unlockOnUserGesture);

            this.unlockBrowserNotificationSound();
        };

        window.addEventListener('pointerdown', unlockOnUserGesture, { once: true });
        window.addEventListener('keydown', unlockOnUserGesture, { once: true });
    }

    private unlockBrowserNotificationSound(): void {
        const audioElement = this.getBrowserAudioElement();

        if (audioElement) {
            audioElement.muted = true;
            audioElement.play()
                .then(() => {
                    audioElement.pause();
                    audioElement.currentTime = 0;
                    audioElement.muted = false;
                    this.browserAudioUnlocked = true;
                    this.browserAudioUnlockListenersRegistered = false;
                })
                .catch(() => {
                    audioElement.muted = false;
                    this.browserAudioUnlockListenersRegistered = false;
                    this.registerBrowserAudioUnlock();
                });
        }
    }

    private async playBrowserNotificationSound(): Promise<void> {
        if (!this.browserAudioUnlocked) {
            return;
        }

        const playedElementSound = await this.playBrowserAudioElementSound();
        if (playedElementSound) {
            return;
        }
    }

    private async playBrowserAudioElementSound(): Promise<boolean> {
        const audioElement = this.getBrowserAudioElement();
        if (!audioElement) {
            return false;
        }

        try {
            audioElement.pause();
            audioElement.currentTime = 0;
            await audioElement.play();
            return true;
        } catch (error) {
            return false;
        }
    }

    private getBrowserAudioElement(): HTMLAudioElement | null {
        if (typeof window === 'undefined') {
            return null;
        }

        if (this.browserAudioElement) {
            return this.browserAudioElement;
        }

        try {
            this.browserAudioElement = new Audio('/assets/sounds/notification-pop.mp3');
            this.browserAudioElement.preload = 'auto';
            this.browserAudioElement.volume = 0.8;
            return this.browserAudioElement;
        } catch (error) {
            console.error('Failed to initialize browser notification audio:', error);
            return null;
        }
    }

    private async ensureMicrophonePermission(): Promise<boolean> {
        if (this.isNativeAndroid()) {
            try {
                const result = await TwilioVoice.ensurePermissions();
                return result?.microphoneGranted === true;
            } catch (error) {
                console.error('Native microphone permission not granted:', error);
                return false;
            }
        }

        if (
            !navigator?.mediaDevices ||
            typeof navigator.mediaDevices.getUserMedia !== 'function'
        ) {
            return false;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((track) => track.stop());
            return true;
        } catch (error) {
            console.error('Microphone permission not granted:', error);
            return false;
        }
    }

    private async scheduleNotification(
        notification: LocalNotificationSchema
    ): Promise<void> {
        try {
            await LocalNotifications.schedule({
                notifications: [
                    {
                        ...notification,
                        schedule: { at: new Date(Date.now() + 250) },
                    },
                ],
            });
        } catch (error) {
            console.error('Failed to schedule local notification:', error);
        }
    }

    private async clearNotificationsByScope(scope: NotificationScope): Promise<void> {
        if (!this.isNativePlatform()) {
            return;
        }

        try {
            const delivered = await LocalNotifications.getDeliveredNotifications();
            const deliveredMatches = delivered.notifications.filter(
                (notification: any) => notification?.extra?.scope === scope
            );

            if (deliveredMatches.length > 0) {
                await LocalNotifications.removeDeliveredNotifications({
                    notifications: deliveredMatches,
                });
            }

            const pending = await LocalNotifications.getPending();
            const pendingMatches = pending.notifications
                .filter((notification: any) => notification?.extra?.scope === scope)
                .map((notification: any) => ({ id: notification.id }));

            if (pendingMatches.length > 0) {
                await LocalNotifications.cancel({
                    notifications: pendingMatches,
                });
            }
        } catch (error) {
            console.error(`Failed to clear ${scope} notifications:`, error);
        }
    }

    private generateId(): number {
        return Math.floor(Date.now() % 2147483647);
    }

    private navigateToConversation(conversationId: number): void {
        this.ngZone.run(() => {
            console.log(`[LocalNotificationRoute] Navigating to conversation ${conversationId}.`);
            this.router
                .navigate(['conversations'], {
                    queryParams: { id: conversationId },
                })
                .then((navigated) => {
                    console.log(
                        `[LocalNotificationRoute] Navigation result=${navigated}, currentUrl=${this.router.url}`
                    );
                })
                .catch((error) => {
                    console.error('[LocalNotificationRoute] Failed to route notification to conversation:', error);
                });
        });
    }

    private getConversationId(payload: any): number | null {
        const sources = [
            payload,
            payload?.extra,
            payload?.data,
            payload?.extra?.data,
            payload?.data?.data,
        ];

        for (const source of sources) {
            const conversationId = this.parseConversationId(
                source?.conversation_id ??
                source?.conversationId ??
                source?.ConversationId ??
                source?.conversation?.id
            );
            if (conversationId) {
                return conversationId;
            }
        }

        return null;
    }

    private parseConversationId(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }
}
